import React, { useCallback, useContext, useState } from 'react';
import { TranslationContext } from '../../../contexts/TranslationContext';
import { NavPage } from '../../../services/NavService';
import { TranslationService } from '../../../services/TranslationService';
import { CreateKeysRequest, CreateLanguageRequest, ILanguage, TranslationAdminClient, TranslationKey } from '../../../services/WebApiClient';
import { useClient } from '../../../util/useClient';
import { State } from '../../../util/useState2';
import { GenericEditor } from '../GenericEditor';
import { SingleLanguageEditor } from './SingleLanguageEditor';


export function TranslationEditor(props: {
    showTranslationKeys: State<boolean>
}) {

    const client = useClient(TranslationAdminClient);
    const [updateCount, setUpdateCount] = useState(0);
    const tc = useContext(TranslationContext);

    const getterFunc = useCallback(async () => {
        const response = await client.getLanguages();
        return {
            languages: response.languages,
            updateCount
        };
    },
        [client, updateCount]
    )

    const unreportedKeys = Object.keys(TranslationService.unknownTranslations)
        .filter(k => !TranslationService.unknownTranslations[k]?.reported)
        .length;

    function editorFunc(lang: ILanguage, onChange: () => void, editorUpdateCount: number) {
        return (
            <SingleLanguageEditor
                language={lang}
                key={`${lang.id} ${updateCount} ${editorUpdateCount}`}
                onChange={onChange}
            />
        );
    }

    async function createMissingKeys() {
        try {
            await client.createkeys(new CreateKeysRequest({
                translationKeys: Object.keys(TranslationService.unknownTranslations)
                    .filter(k => TranslationService.unknownTranslations[k].reported === false)
                    .map(key => new TranslationKey({
                        key,
                        defaultValue: TranslationService.unknownTranslations[key].defaultValue
                    }))
            }));

            for (let k of Object.keys(TranslationService.unknownTranslations)) {
                const val = TranslationService.unknownTranslations[k];
                val.reported = true;
            }
        }
        finally {
            await Promise.resolve();
            setUpdateCount(updateCount + 1);
        }
    }

    const leftToolbox = (
        <div>
            {unreportedKeys > 0 &&
                <div>
                    <button className="btn btn-block btn-secondary"
                        style={{ marginTop: "3em" }}
                        onClick={createMissingKeys}
                >
                    {tc.translator("admin translations sidebar create new keys", "Opret {unreportedKeys} nye nøgler", { unreportedKeys: unreportedKeys.toString() })}
                    </button>
                </div>
            }
            <div className="form-group form-check">
                <input type="checkbox"
                    className="form-check-input"
                    checked={props.showTranslationKeys.value}
                    onChange={evt => props.showTranslationKeys.set(evt.target.checked)}
                    id="chk-show-translation-keys"
                />
                <label htmlFor="chk-show-translation-keys">
                    {tc.translator("admin translations sidebar show keys", "Vis nøgler")}
                </label>
            </div>
        </div>
    );

    return (
        <GenericEditor<ILanguage>
            nameFunc={lang => {
                const unknownCount = lang.translations
                    .filter(t => t.value === null)
                    .length;
                if (unknownCount === 0)
                    return lang.name;
                return (
                    <span>
                        <span className="badge badge-warning">
                            {unknownCount}
                        </span>
                        <span style={{ marginLeft: ".5em" }}>
                            {lang.name}
                        </span>
                    </span>
                );
            }}
            getterFunc={async () => (await getterFunc()).languages}
            editorFunc={editorFunc}
            key={updateCount}
            navPage={NavPage.Admin.Translation}
            leftToolbox={leftToolbox}
            newHandler={{
                buttonName: tc.translator("admin translations sidebar new lang button", "Nyt sprog"),
                onClick: async () => {
                    const newName = window.prompt(
                        tc.translator("admin translations sidebar new lang prompt", "Sprognavn"),
                        tc.translator("admin translations sidebar new lang prompt default", "Volapyk")
                    );
                    if (!newName)
                        return;
                    const response = await client.createLanguage(new CreateLanguageRequest({ name: newName }));
                    return response.languageId;
                }
            }}

        />
    );
}
